import React, { Component, ReactElement } from 'react';

import { AccordionAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { array, arrayOf, bool, element, eventListener, object, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { CloseEvent, OpenEvent } from '@philips-emr/tws-core/lib/component/types';
import { classNames, getSlot, isFunction, propagateData } from '@philips-emr/tws-utils';

import { CollapseArrow } from '..';

import './accordion.scss';

const headerAcceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.LAYOUT,
];

const contentAcceptedTypes = [
  ComponentType.AGGREGATOR,
  ComponentType.BEHAVIOR,
  ComponentType.DATA_DISPLAY,
  ComponentType.FORM,
  ComponentType.LAYOUT,
  ComponentType.LIST,
];

export interface AccordionProps {
  children: ReactElement[];
  className?: string;
  data?: object | object[];
  initialCollapsed?: boolean;
  onBeforeClose?: (event: CloseEvent) => void;
  onClose?: (event: CloseEvent) => void;
  onOpen?: (event: OpenEvent) => void;
  uuid: string;
}

interface AccordionState {
  collapsed: boolean;
}

@AwesomeComponent({ type: ComponentType.AGGREGATOR, api: AccordionAPI })
class Accordion extends Component<AccordionProps, AccordionState> {
  state: AccordionState = {
    collapsed: true,
  };

  static propTypes = {
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    className: string,
    data: oneOfType([array, object]).notEnumerable(),
    initialCollapsed: bool,
    onClose: eventListener,
    onOpen: eventListener,
    uuid: string,
  };

  static defaultProps = {
    initialCollapsed: true,
  };

  componentDidMount(): void {
    const { initialCollapsed } = this.props;

    if (typeof initialCollapsed !== 'undefined') {
      this.setState({ collapsed: initialCollapsed });
    }
  }

  onClick = (): void => {
    const { onBeforeClose, onClose, onOpen } = this.props;
    const { collapsed } = this.state;

    let aborted = false;

    if (isFunction(onBeforeClose) && !collapsed) {
      onBeforeClose(new CloseEvent({
        abort: (): void => {
          aborted = true;
        },
      }));
    }

    if (aborted) {
      return;
    }

    const fn = (): void => {
      const { collapsed } = this.state;

      if (collapsed) {
        isFunction(onClose) && onClose(new CloseEvent());
      } else {
        isFunction(onOpen) && onOpen(new OpenEvent());
      }
    };

    this.setState((prevState) => ({ collapsed: !prevState.collapsed }), fn);
  };

  getClassName = (): string => {
    const { className } = this.props;

    const classes = {
      'c-accordion': true,
      [`${className}`]: Boolean(className),
    };

    return classNames(classes);
  };

  renderHeader(): ReactElement | null {
    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }

    const { children, data, uuid } = this.props;
    const { collapsed } = this.state;

    const content = getSlot(children, 'header');

    return (
      <div className="c-accordion__header">
        <Container
          acceptedTypes={headerAcceptedTypes}
          className="c-accordion__container"
          onClick={this.onClick}
          slot="header"
          uuid={uuid}
        >
          {propagateData(content, data)}
        </Container>
        <CollapseArrow collapsed={collapsed} onClick={this.onClick} />
      </div>
    );
  }

  renderContent(): ReactElement | null {
    const { collapsed } = this.state;

    if (collapsed) {
      return null;
    }

    const Container = ContainerProvider.provide(this);

    if (!Container) {
      return null;
    }

    const { children, data, uuid } = this.props;

    const content = getSlot(children, 'content');

    return (
      <Container
        acceptedTypes={contentAcceptedTypes}
        className="c-accordion__content"
        slot="content"
        uuid={uuid}
      >
        {propagateData(content, data)}
      </Container>
    );
  }

  render(): ReactElement {
    return (
      <div className={this.getClassName()} data-testid="accordion">
        {this.renderHeader()}
        {this.renderContent()}
      </div>
    );
  }
}

export default Accordion;
